import React, { useState } from "react";
import { addDays } from "date-fns";
import {
  Button,
  DataTable,
  DataTableColumn,
  FilterBar,
  Page,
  hasActiveFilters,
  useFilterSortPagination,
} from "@sscs/ui";
import { formatCount, getPageItems, getQueryStatus } from "@sscs/utils";
import { PERMISSION } from "Constants";
import { useAllowedTo, useDialog, useRedirect } from "Hooks";
import AuditLog from "../../components/AuditLog/AuditLog";
import DateTimeRangeFilter from "../../components/DateTimeRangeFilter/DateTimeRangeFilter";
import ExportDialog from "../../components/ExportDialog/ExportDialog";
import ExportsTable from "../../components/ExportsTable/ExportsTable";
import {
  useGetAuditExportsQuery,
  useSearchAuditLogsQuery,
} from "../../services/audit.api";
import { SearchAuditLogsResponse } from "../../types";
import Filter from "./Filter";

const COLUMNS: DataTableColumn[] = [
  { id: "info", label: "Info", width: "70px" },
  { id: "timestamp", label: "Timestamp", width: "150px" },
  { id: "user", label: "User", width: "350px" },
  { id: "action", label: "Action", width: "40%" },
  { id: "target", label: "Target", width: "60%" },
  { id: "outcome", label: "Outcome", width: "150px" },
];

const AuditLogs = () => {
  const canViewAuditLogs = useAllowedTo({
    permissions: [PERMISSION.VIEW_AUDIT],
  });
  useRedirect({
    to: "/page-not-found",
    when: !canViewAuditLogs,
  });

  const [endDate, setEndDate] = useState(new Date());
  const [startDate, setStartDate] = useState(addDays(endDate, -1));

  // Dummy sort, not used
  const { filter, pagination, handleFilterChange, handlePaginationChange } =
    useFilterSortPagination({}, { property: "log_id", reverse: false });

  const [focusedRow, setFocusedRow] = useState<string | null>(null);

  const exportDialog = useDialog();

  // Same args as ExportsTable's first page, so RTK Query shares one request.
  const { data: exportJobs } = useGetAuditExportsQuery({
    limit: 10,
    offset: 0,
  });
  const exportCount = exportJobs?.match_count ?? 0;

  // Used to force expanded rows (AuditLog) to collapse when state changes
  const baseKey = JSON.stringify({
    pagination: pagination,
    filters: filter,
    startDate: startDate,
    endDate: endDate,
  });

  const {
    data: auditLogs = {
      entries: [],
    } satisfies SearchAuditLogsResponse,
    isFetching,
    isLoading,
    isError,
    error,
  } = useSearchAuditLogsQuery({
    body: {
      datetime_start: startDate.toISOString(),
      datetime_end: endDate.toISOString(),
      filters: filter,
    },
  });

  const isResultsPending = isFetching || isLoading;
  // The API caps the result set at 1000; keep the "narrow your search" hint when
  // capped, otherwise the plain "N results".
  const cappedResultsLabel =
    auditLogs.entries.length >= 1000
      ? "Showing the first 1000 results. Apply more filters to narrow your search."
      : undefined;

  return (
    <Page.Root>
      <Page.Header
        title="Audit logs"
        details={[formatCount(exportCount, "export")]}
        actions={<Button onClick={exportDialog.open}>Export</Button>}
        expand={{
          expandLabel: "Show exports",
          collapseLabel: "Hide exports",
          children: <ExportsTable />,
          show: exportCount > 0,
        }}
      />
      <Page.Filters
        found={{
          id: "audit-clear-filters",
          // Leave count/label undefined while the query is in flight so the
          // summary row keeps its height and fills in place when data lands.
          count: isResultsPending ? undefined : auditLogs.entries.length,
          label: isResultsPending ? undefined : cappedResultsLabel,
          initialFilters: {},
          activeFilters: filter,
          onClear: () => handleFilterChange({}),
        }}
      >
        <FilterBar
          filters={[
            {
              id: "time-range",
              grow: 0,
              control: (
                <DateTimeRangeFilter
                  id="time-range-filter"
                  startDate={startDate}
                  endDate={endDate}
                  onSave={(startDate, endDate) => {
                    setStartDate(startDate);
                    setEndDate(endDate);
                    handlePaginationChange({
                      currentPage: 1,
                      itemsPerPage: pagination.itemsPerPage,
                    });
                  }}
                />
              ),
            },
            {
              id: "filters",
              minWidth: "0",
              control: (
                <Filter filters={filter} onFilterChange={handleFilterChange} />
              ),
            },
          ]}
        />
      </Page.Filters>
      <Page.Body>
        <DataTable
          key={baseKey}
          columns={COLUMNS}
          empty={{
            subject: "entries",
            filters: {
              active: hasActiveFilters({}, filter),
              onReset: () => handleFilterChange({}),
            },
          }}
          status={getQueryStatus({
            isFetching,
            isLoading,
            isError,
            error,
            data: auditLogs,
          })}
          pagination={{
            id: "audit-pagination",
            itemCount: auditLogs?.entries.length ?? 0,
            pagination,
            onChange: handlePaginationChange,
          }}
        >
          {getPageItems(
            auditLogs?.entries,
            pagination.currentPage,
            pagination.itemsPerPage,
          ).map((log) => (
            <AuditLog
              key={log.log_id}
              log={log}
              focused={focusedRow === log.log_id}
              onClick={() => {
                setFocusedRow(log.log_id);
              }}
            />
          ))}
        </DataTable>
      </Page.Body>
      <ExportDialog
        isOpen={exportDialog.isOpen}
        onClose={exportDialog.close}
        datetimeStart={startDate}
        datetimeEnd={endDate}
        filters={filter}
      />
    </Page.Root>
  );
};

export default AuditLogs;
